'use client'

import PropTypes from 'prop-types'
import { useState, useEffect, useCallback } from 'react'
import { ActivitySelect } from '@repo/ui/ActivitySelect'
import { TextEntry } from '@repo/ui/TextEntry'
import { TimeRecordingRate } from '../TimeRecordingRate/TimeRecordingRate'
import { useTimeRecordingStore } from '@repo/store/useTimeRecordingStore'
import { FormSectionTitle } from '@repo/ui/FormSectionTitle'
import { TextLinkButton } from '@repo/ui/TextLinkButton'
import { FormPanel } from '@repo/ui/FormPanel'
import { useDebounce } from '@uidotdev/usehooks'
import { useGlobalSettingsStore } from '@repo/store/useGlobalSettingsStore'
import MUIAlert from '@mui/material/Alert'

export const TimeRecordingActivityRow = ({
  matter,
  remunerationTypeId,
  feeEarnerId,
  selectedTimer,
  clientNo,
  matterNo,
  onChange,
  index,
  formMode,
  rowCount,
  removeActivity,
  onActivityError,
  onRequiredState = () => {}
}) => {
  const { fetchRate } = useTimeRecordingStore()

  const defaultActivityId = selectedTimer?.timeRecordingActivityId || null
  const [timeRecordingActivityId, setTimeRecordingActivityId] = useState(defaultActivityId)
  const debouncedTimeRecordingActivityId = useDebounce(timeRecordingActivityId, 300)

  const defaultActivityDescription = selectedTimer?.activityDescription || null
  const [timeRecordingActivityDescription, setTimeRecordingActivityDescription] = useState(defaultActivityDescription)

  const [rateDisabled, setRateDisabled] = useState(false)

  const [description, setDescription] = useState(selectedTimer?.description || '')

  const [rateType, setRateType] = useState(null)
  const [rateAmount, setRateAmount] = useState(null)
  const [timeAmount, setTimeAmount] = useState(null)
  const [quantity, setQuantity] = useState(null)

  const [activityError, setActivityError] = useState(null)

  const { privateOrLegalAid, workflowTypeId, crmSchemeId, branchId } = matter || {}

  const { isOutlookAddin } = useGlobalSettingsStore()

  const setTimeRecordingActivity = (newValue) => {
    setTimeRecordingActivityId(newValue?.timeRecordingActivityId || null)
    setTimeRecordingActivityDescription(newValue?.timeRecordingActivityDescription || null)
  }

  useEffect(() => {
    // this useEffect resets the activity if remunerationTypeId changes

    // we generally need to reset the activityId when the remunerationTypeId changes
    // but not when we first load the data from the timer
    // so we return if the values are the same as in the timer

    const remunerationTypeMatchesTimer =
      selectedTimer?.remunerationTypeId && selectedTimer.remunerationTypeId === remunerationTypeId

    const activityIdMatchesTimer =
      selectedTimer?.timeRecordingActivityId && selectedTimer.timeRecordingActivityId === timeRecordingActivityId

    const shouldSkipUpdate = remunerationTypeMatchesTimer && activityIdMatchesTimer

    if (!shouldSkipUpdate) {
      setTimeRecordingActivity({
        timeRecordingActivityId: null,
        timeRecordingActivityDescription: ''
      })
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remunerationTypeId])

  const setNewDescription = (newDesc) => {
    setDescription(newDesc)
  }

  useEffect(() => {
    const newValue = {
      timeRecordingActivity: {
        timeRecordingActivityId,
        timeRecordingActivityDescription
      },
      rateType,
      rateAmount,
      timeAmount,
      quantity,
      description
    }

    if (onChange) onChange(newValue, index)
  }, [
    timeRecordingActivityId,
    timeRecordingActivityDescription,
    description,
    rateType,
    rateAmount,
    timeAmount,
    quantity,
    onChange,
    index
  ])

  useEffect(() => {
    const newDescription = getDefaultDescription({
      description,
      timeRecordingActivityId,
      timeRecordingActivityDescription
    })
    if (newDescription) setDescription(newDescription)
  }, [description, timeRecordingActivityId, timeRecordingActivityDescription, formMode])

  const getRate = useCallback(
    async ({ clientNo, matterNo, feeEarnerId, debouncedTimeRecordingActivityId, remunerationTypeId }) => {
      const isDisabled = !clientNo || !feeEarnerId || !debouncedTimeRecordingActivityId || !remunerationTypeId
      setRateDisabled(isDisabled)
      if (isDisabled) return

      const res = await fetchRate({
        clientNo,
        matterNo,
        feeEarnerId,
        timeRecordingActivityId: debouncedTimeRecordingActivityId,
        remunerationTypeId
      })

      if (!res) return

      const { rateType, rateAmount, timeAmount } = res

      setRateType(rateType)
      setRateAmount(rateAmount)
      setTimeAmount(timeAmount)
    },
    [fetchRate]
  )

  // fetch data
  useEffect(() => {
    handleActivityError(null)
    getRate({ clientNo, matterNo, feeEarnerId, debouncedTimeRecordingActivityId, remunerationTypeId })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientNo, matterNo, feeEarnerId, debouncedTimeRecordingActivityId])

  useEffect(() => {
    if (!description || !timeRecordingActivityId || !timeRecordingActivityDescription) {
      onRequiredState(false)
    } else {
      onRequiredState(true)
    }
  }, [description, timeRecordingActivityId, timeRecordingActivityDescription, onRequiredState])

  const handleActivityError = (error) => {
    setActivityError(error)
    if (onActivityError) {
      onActivityError(error)
    }
  }

  if (activityError) {
    return (
      <MUIAlert data-testid="TimeRecordingActivityRow" severity="error">
        {activityError}
      </MUIAlert>
    )
  }

  let timerMinutes = null

  if (selectedTimer) {
    const seconds = selectedTimer.runningTotalInSeconds || selectedTimer.secondsRun || 0
    timerMinutes = seconds / 60
  }

  return (
    <div data-testid="TimeRecordingActivityRow">
      <FormPanel>
        <FormSectionTitle
          title={`Activity ${rowCount > 1 ? index + 1 : ''}`}
          endElement={
            index > 0 ? (
              <TextLinkButton onClick={() => removeActivity(index)} title="Remove" sx={{ width: 'auto' }} />
            ) : null
          }
        />

        <ActivitySelect
          value={timeRecordingActivityId ? { timeRecordingActivityId, timeRecordingActivityDescription } : null}
          onChange={setTimeRecordingActivity}
          privateOrLegalAid={privateOrLegalAid || ''}
          workflowTypeId={workflowTypeId || ''}
          crmSchemeId={crmSchemeId || 0}
          branchId={branchId || 0}
          remunerationTypeId={remunerationTypeId}
          sx={{ width: '100%' }}
          menuWidth={isOutlookAddin ? '260px' : null}
          required={formMode !== 'createTimer'}
          menuZindex={9999}
          onActivityError={handleActivityError}
          index={index}
          formMode={formMode}
        />

        <TextEntry value={description} onChange={setNewDescription} required={formMode !== 'createTimer'} limit={60} />

        {formMode !== 'createTimer' ? (
          <TimeRecordingRate
            rateType={rateType}
            timeAmount={timeAmount}
            rateAmount={rateAmount}
            quantity={quantity}
            onChangeQuantity={setQuantity}
            timerMinutes={timerMinutes}
            index={index}
            isDisabled={rateDisabled}
          />
        ) : null}
      </FormPanel>
    </div>
  )
}

const getDefaultDescription = ({ description, timeRecordingActivityId, timeRecordingActivityDescription }) => {
  if (description) return
  if (!timeRecordingActivityId || !timeRecordingActivityDescription) return

  // yes, we're actually inserting 8 spaces here for backward compatibility with Osprey 1
  let defaultDescription = `${timeRecordingActivityId}        ${timeRecordingActivityDescription}`

  if (defaultDescription.length > 60) {
    defaultDescription = defaultDescription.substring(0, 60)
  }

  return defaultDescription
}

TimeRecordingActivityRow.propTypes = {
  matter: PropTypes.object,
  remunerationTypeId: PropTypes.any,
  feeEarnerId: PropTypes.any,
  onChange: PropTypes.func,
  selectedTimer: PropTypes.object,
  clientNo: PropTypes.string,
  matterNo: PropTypes.any,
  index: PropTypes.number,
  formMode: PropTypes.string,
  rowCount: PropTypes.number,
  removeActivity: PropTypes.func,
  onActivityError: PropTypes.func,
  defaultActivity: PropTypes.object,
  onRequiredState: PropTypes.func
}
